import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Loader2, Medal, Users } from 'lucide-react'
import type { BracketPlayer, BracketTemplate } from './types'
import { getInitials } from './utils'

interface StandingsTableProps {
  players: BracketPlayer[]
  playersLoading: boolean
  template: BracketTemplate | null
}

export function StandingsTable({
  players,
  playersLoading,
  template,
}: StandingsTableProps) {
  if (!template) return null

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Medal className="h-5 w-5 text-primary" />
            {template.name} Standings
          </CardTitle>
          <span className="text-sm text-muted-foreground">
            {players.length} player{players.length !== 1 ? 's' : ''}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {playersLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : players.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">No players in this bracket yet</p>
          </div>
        ) : (
          <>
            {/* Desktop View - Table-like Grid */}
            <div className="hidden sm:block space-y-1">
              {/* Header */}
              <div className="grid grid-cols-12 gap-2 text-xs text-muted-foreground font-medium px-2 pb-2">
                <div className="col-span-1">#</div>
                <div className="col-span-6">Player</div>
                <div className="col-span-2 text-center">W-L</div>
                <div className="col-span-3 text-right">Pts</div>
              </div>
              
              {/* Players - sorted by points (backend already does this) */}
              {players.map((player, index) => (
                  <div
                    key={player.userId}
                    className={`grid grid-cols-12 gap-2 items-center py-2 px-2 rounded-lg hover:bg-muted/50 ${
                      index < 3 ? 'border-l-2' : ''
                    } ${
                      index === 0 ? 'border-l-yellow-500' :
                      index === 1 ? 'border-l-gray-400' :
                      index === 2 ? 'border-l-amber-600' : ''
                    }`}
                  >
                    <div className="col-span-1 font-bold text-muted-foreground">
                      {index + 1}
                    </div>
                    <div className="col-span-6 flex items-center gap-2 min-w-0">
                      <Avatar className="h-8 w-8 shrink-0">
                        <AvatarImage src={player.avatar} alt={player.displayName} />
                        <AvatarFallback className="text-xs">
                          {getInitials(player.displayName)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="truncate">{player.displayName}</span>
                    </div>
                    <div className="col-span-2 text-center text-sm">
                      <span className="text-green-600 dark:text-green-400">{player.wins}</span>
                      <span className="text-muted-foreground">-</span>
                      <span className="text-red-600 dark:text-red-400">{player.losses}</span>
                    </div>
                    <div className="col-span-3 text-right font-medium">
                      {player.points}
                    </div>
                  </div>
                ))}
            </div>

            {/* Mobile View - Compact Cards */}
            <div className="sm:hidden space-y-2">
              {players.map((player, index) => (
                  <div
                    key={player.userId}
                    className={`flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 ${
                      index < 3 ? 'border-l-2' : 'border-l-2 border-l-transparent'
                    } ${
                      index === 0 ? 'border-l-yellow-500 bg-yellow-500/5' :
                      index === 1 ? 'border-l-gray-400 bg-gray-500/5' :
                      index === 2 ? 'border-l-amber-600 bg-amber-500/5' : ''
                    }`}
                  >
                    {/* Rank */}
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                      index === 0 ? 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400' :
                      index === 1 ? 'bg-gray-500/20 text-gray-600 dark:text-gray-400' :
                      index === 2 ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {index + 1}
                    </div>
                    
                    {/* Avatar & Name */}
                    <Avatar className="h-9 w-9 shrink-0">
                      <AvatarImage src={player.avatar} alt={player.displayName} />
                      <AvatarFallback className="text-xs">
                        {getInitials(player.displayName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium truncate block text-sm">{player.displayName}</span>
                      <div className="text-xs text-muted-foreground">
                        <span className="text-green-600 dark:text-green-400">{player.wins}W</span>
                        <span className="mx-1">-</span>
                        <span className="text-red-600 dark:text-red-400">{player.losses}L</span>
                      </div>
                    </div>
                    
                    {/* Points */}
                    <div className="text-right shrink-0">
                      <span className="text-sm font-semibold">{player.points}</span>
                      <p className="text-[10px] text-muted-foreground">pts</p>
                    </div>
                  </div>
                ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
